import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { AiTier } from '@siliconvault/shared';

export interface AiUsage {
  count: number;
  limit: number;
  periodEnd: string | null;
}

export interface AppMode {
  mode: 'web' | 'desktop';
  aiConfigured: boolean;
  communityEnabled: boolean;
  multiVaultEnabled: boolean;
  aiTier: AiTier;
  aiUsage?: AiUsage;
}

const DEFAULT_MODE: AppMode = {
  mode: 'web',
  aiConfigured: false,
  communityEnabled: true,
  multiVaultEnabled: false,
  aiTier: 'free',
};

interface AppModeState extends AppMode {
  isModeLoaded: boolean;
}

const AppModeContext = createContext<AppModeState>({ ...DEFAULT_MODE, isModeLoaded: false });

export function AppModeProvider({ children }: { children: ReactNode }) {
  const { data, isFetched } = useQuery<AppMode>({
    queryKey: ['app-mode'],
    queryFn: () =>
      fetch('/api/mode', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : DEFAULT_MODE))
        .catch(() => DEFAULT_MODE),
    staleTime: Infinity, // mode never changes mid-session except after AI config save
    retry: false,
  });

  const resolved = data ?? DEFAULT_MODE;
  const value = useMemo<AppModeState>(
    () => ({ ...resolved, isModeLoaded: isFetched }),
    [resolved, isFetched],
  );

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode(): AppMode & { hasAiAccess: boolean; isModeLoaded: boolean } {
  const mode = useContext(AppModeContext);
  // AI access: own key configured, Pro tier, or free tier with remaining managed scans
  const hasAiAccess = mode.aiConfigured
    || mode.aiTier === 'pro'
    || (mode.aiTier === 'free' && !!mode.aiUsage && mode.aiUsage.count < mode.aiUsage.limit);
  return useMemo(
    () => ({ ...mode, hasAiAccess, isModeLoaded: mode.isModeLoaded }),
    [mode, hasAiAccess],
  );
}
